import { createContext, useContext, type Accessor } from "solid-js";
import type { Project } from "../api";
import type { ProjectId, ProjectQuotaStatus, TenantId } from "../api/types";

interface ProjectContextValue {
  projectId: Accessor<ProjectId>;
  projectPrefix: Accessor<string>;
  /** Org that owns the selected project — undefined until projects load. */
  projectTenantId: Accessor<TenantId | undefined>;
  /**
   * FLOW-929 D1: the shell's single `useProjectQuotaStatus` read, so the
   * Settings meter and the header banner can never disagree. Optional and
   * non-throwing for the same reason as `useProjectTenantId` — ChatDrawer
   * and the page tests build a provider without it.
   */
  projectQuota?: Accessor<ProjectQuotaStatus | undefined>;
  onProjectCreated?: (project: Project) => void;
}

const ProjectContext = createContext<ProjectContextValue>();

export const ProjectProvider = ProjectContext.Provider;

export function useProjectId(): Accessor<ProjectId> {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error("useProjectId must be used within a ProjectProvider");
  }
  return ctx.projectId;
}

export function useProjectPrefix(): Accessor<string> {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error("useProjectPrefix must be used within a ProjectProvider");
  }
  return ctx.projectPrefix;
}

/**
 * Unlike the hooks above this one does not throw outside a provider. The
 * degraded-output badge (FLOW-794) renders on surfaces that mount outside
 * `ProjectProvider` — draft enrichment in the welcome wizard — where an
 * unresolved tenant is an ordinary state, not a programming error. Callers
 * must treat `undefined` as "quota unknown" rather than "quota fine".
 */
export function useProjectTenantId(): Accessor<TenantId | undefined> {
  const ctx = useContext(ProjectContext);
  return ctx?.projectTenantId ?? (() => undefined);
}

/**
 * Non-throwing for the same reason as `useProjectTenantId`: the
 * degraded-output badge reads the *project's* quota when the project ceiling
 * is what bound the output to the fast tier (FLOW-930), and it mounts on
 * surfaces that have no `ProjectProvider`. `undefined` means "quota unknown".
 */
export function useOptionalProjectId(): Accessor<ProjectId | undefined> {
  const ctx = useContext(ProjectContext);
  return ctx?.projectId ?? (() => undefined);
}

/**
 * Non-throwing for the same reason as `useProjectTenantId`: `ChatDrawer` and
 * ~20 unit tests build a `ProjectProvider` without this field, and it mounts
 * on surfaces outside `ProjectProvider` entirely.
 */
export function useProjectQuota(): Accessor<ProjectQuotaStatus | undefined> {
  const ctx = useContext(ProjectContext);
  return ctx?.projectQuota ?? (() => undefined);
}

export function useProjectCreated(): (project: Project) => void {
  const ctx = useContext(ProjectContext);
  if (!ctx?.onProjectCreated) {
    return () => {};
  }
  return ctx.onProjectCreated;
}
